import React, { useState } from 'react';

import { formatPhaseCredits } from '../hooks/usePhaseBuildState';
import type {
  BuildPhase,
  PhaseBuildExecutionState,
} from '../services/buildPhaseService';

import { BillingModal } from './BillingModal';
import './PhaseBuildStatus.css';

interface PhaseBuildStatusProps {
  state: PhaseBuildExecutionState | null;
  displayPhase?: BuildPhase | null;
  compact?: boolean;
  className?: string;
}

const CompletedPhaseStatus: React.FC<{ className: string }> = ({ className }) => (
  <div className={`phase-build-status phase-build-status--complete ${className}`.trim()}>
    <i className="fa-solid fa-circle-check" aria-hidden="true" />
    <span>All build phases are complete.</span>
  </div>
);

interface PhaseCreditsProps {
  state: PhaseBuildExecutionState;
  onAddCredits: () => void;
  onUpgrade: () => void;
}

const CreditIcon: React.FC<{ warning?: boolean }> = ({ warning = false }) => (
  <span className={`phase-build-status__credit-icon${warning ? ' phase-build-status__credit-icon--warning' : ''}`}>
    <i className={`fa-solid ${warning ? 'fa-coins' : 'fa-circle-info'}`} aria-hidden="true" />
  </span>
);

const getPhaseBlockerText = (state: PhaseBuildExecutionState): string | null => {
  const count = state.blocking_task_count;

  if (state.blocking_reason === 'build_in_progress') return 'Build in progress';
  if (state.blocking_reason === 'dependencies') {
    return `${count} task${count === 1 ? '' : 's'} blocked by dependencies`;
  }
  if (state.blocking_reason !== 'needs_attention') return null;
  if (count === 0) return 'Phase tasks need attention';

  return `${count} task${count === 1 ? '' : 's'} need attention`;
};

const PhaseCreditSummary: React.FC<{ state: PhaseBuildExecutionState }> = ({ state }) => (
  <div className="phase-build-status__credit-summary">
    <CreditIcon warning />
    <span className="phase-build-status__credit-copy">
      <strong>{formatPhaseCredits(state.credit_shortfall)} more credits</strong>
      <small>
        {formatPhaseCredits(state.available_credits)} available ·{' '}
        {formatPhaseCredits(state.required_credits)} required
      </small>
    </span>
  </div>
);

const PhaseCredits: React.FC<PhaseCreditsProps> = ({ state, onAddCredits, onUpgrade }) => {
  const blockerText = getPhaseBlockerText(state);
  const hasShortfall = state.credit_shortfall > 0;

  if (state.is_unlimited) {
    return (
      <div className="phase-build-status__credit-summary">
        <CreditIcon />
        <span className="phase-build-status__credit-copy">
          <strong>{blockerText || 'Unlimited credits'}</strong>
          <small>
            {blockerText
              ? 'Resolve before building'
              : `${formatPhaseCredits(state.required_credits)} credits of usage will be recorded`}
          </small>
        </span>
      </div>
    );
  }

  if (!hasShortfall) {
    return (
      <div className="phase-build-status__credit-summary">
        <CreditIcon />
        <span className="phase-build-status__credit-copy">
          <strong>{blockerText || `${formatPhaseCredits(state.required_credits)} credits`}</strong>
          <small>{blockerText ? 'Resolve before building' : 'Required for this phase'}</small>
        </span>
      </div>
    );
  }

  return (
    <>
      {blockerText && (
        <span className="phase-build-status__blocker">{blockerText}</span>
      )}
      <PhaseCreditSummary state={state} />
      {state.can_start && (
        <span className="phase-build-status__partial-note">
          {state.affordable_task_count ?? 0} ready · {state.deferred_task_count ?? 0} deferred
        </span>
      )}
      <div className="phase-build-status__credit-actions">
        <button type="button" onClick={onAddCredits}
          className="app-action-btn app-action-btn--warning app-action-btn--compact">
          <i className="fa-solid fa-coins" aria-hidden="true" />
          <span>Add Credits</span>
        </button>
        <button type="button" onClick={onUpgrade}
          className="app-action-btn app-action-btn--upgrade app-action-btn--compact">
          <i className="fa-solid fa-arrow-up-right-dots" aria-hidden="true" />
          <span>Upgrade</span>
        </button>
      </div>
    </>
  );
};

const SelectedPhaseCredits: React.FC<{
  phase: BuildPhase;
  complete: boolean;
}> = ({ phase, complete }) => (
  <div className="phase-build-status__credit-summary">
    <CreditIcon />
    <span className="phase-build-status__credit-copy">
      <strong>
        {complete ? 'Phase complete' : `${formatPhaseCredits(phase.remaining_credits)} credits`}
      </strong>
      <small>{complete ? 'No credits required' : 'Remaining for this phase'}</small>
    </span>
  </div>
);

interface PhaseDetailsProps {
  state: PhaseBuildExecutionState | null;
  phase: BuildPhase;
  phaseComplete: boolean;
  isCurrentPhase: boolean;
  compact?: boolean;
  className?: string;
  onAddCredits: () => void;
  onUpgrade: () => void;
}

interface PhaseProgressProps {
  phase: NonNullable<PhaseBuildExecutionState['current_phase']>;
  progress: number;
}

const PhaseProgress: React.FC<PhaseProgressProps> = ({ phase, progress }) => (
  <div className="phase-build-status__main">
    <div className="phase-build-status__heading">
      <div className="phase-build-status__title">
        <span className="phase-build-status__phase-number">Phase {phase.phase_number}</span>
        <strong title={phase.name}>{phase.name}</strong>
      </div>
      <span className="phase-build-status__task-count">
        {phase.completed_task_count} of {phase.task_count} tasks
      </span>
    </div>
    <div
      className="phase-build-status__track"
      role="progressbar"
      aria-label={`Phase ${phase.phase_number} progress`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progress}
    >
      <span style={{ width: `${progress}%` }} />
    </div>
  </div>
);

const getPhaseStatusClassName = (
  compact: boolean,
  insufficient: boolean,
  className: string,
): string => [
  'phase-build-status',
  compact ? 'phase-build-status--compact' : '',
  insufficient ? 'phase-build-status--insufficient' : '',
  className,
].filter(Boolean).join(' ');

const PhaseDetails: React.FC<PhaseDetailsProps> = ({
  state,
  phase,
  phaseComplete,
  isCurrentPhase,
  compact = false,
  className = '',
  onAddCredits,
  onUpgrade,
}) => {
  const progress = Math.max(0, Math.min(100, phase.completion_percentage || 0));
  const insufficient = isCurrentPhase && Boolean(state?.credit_shortfall);

  return (
    <div className={getPhaseStatusClassName(compact, insufficient, className)}>
      <PhaseProgress phase={phase} progress={progress} />
      <div className="phase-build-status__credits">
        {isCurrentPhase && state && !phaseComplete ? (
          <PhaseCredits
            state={state}
            onAddCredits={onAddCredits}
            onUpgrade={onUpgrade}
          />
        ) : (
          <SelectedPhaseCredits phase={phase} complete={phaseComplete} />
        )}
      </div>
    </div>
  );
};

const getDisplayPhaseDetails = (
  state: PhaseBuildExecutionState | null,
  displayPhase: BuildPhase | null,
) => {
  const phase = displayPhase || state?.current_phase || null;
  const phaseComplete = Boolean(
    phase && (
      phase.status === 'completed'
      || (phase.task_count > 0 && phase.completed_task_count >= phase.task_count)
    )
  );

  return {
    phase,
    phaseComplete,
    isCurrentPhase: phase?.id === state?.current_phase?.id,
  };
};

export const PhaseBuildStatus: React.FC<PhaseBuildStatusProps> = ({
  state,
  displayPhase = null,
  compact = false,
  className = '',
}) => {
  const [billingView, setBillingView] = useState<'credits' | 'plans' | null>(null);
  const { phase, phaseComplete, isCurrentPhase } = getDisplayPhaseDetails(state, displayPhase);

  if (!phase) {
    return state?.all_completed ? <CompletedPhaseStatus className={className} /> : null;
  }
  if (!displayPhase && state?.all_completed) {
    return <CompletedPhaseStatus className={className} />;
  }

  return (
    <>
      <PhaseDetails
        state={state}
        phase={phase}
        phaseComplete={phaseComplete}
        isCurrentPhase={isCurrentPhase}
        compact={compact}
        className={className}
        onAddCredits={() => setBillingView('credits')}
        onUpgrade={() => setBillingView('plans')}
      />
      {billingView && (
        <BillingModal
          initialView={billingView}
          initialCreditAmount={state?.credit_shortfall}
          onClose={() => setBillingView(null)}
        />
      )}
    </>
  );
};
